import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { cn } from './cn'
import { IconChevronUp, IconChevronDown, IconChevronExpand } from './Icons'
import { EmptyState } from './EmptyState'
import { SearchInput } from './SearchInput'
import { SkeletonRows } from './Skeleton'

export interface DataTableColumn<RowT> {
  key: string
  header: ReactNode
  align?: 'start' | 'end'
  /** default true; set false to disable header sort */
  sortable?: boolean
  render?: (row: RowT) => ReactNode
  sortAccessor?: (row: RowT) => string | number
  filterValue?: (row: RowT) => unknown
}

export interface DataTableProps<RowT> {
  columns: DataTableColumn<RowT>[]
  data: RowT[]
  searchable?: boolean
  searchPlaceholder?: string
  /** 0 = no pagination */
  pageSize?: number
  initialSort?: { key: string; dir: 'asc' | 'desc' } | null
  caption?: ReactNode
  rowKey?: (row: RowT, i: number) => string | number
  toolbar?: ReactNode
  className?: string
  /** Show a skeleton instead of rows while data loads. */
  loading?: boolean
  /** Number of skeleton rows shown while loading. Default 6. */
  loadingRows?: number
  /** Load failure. Shows an inline error panel + optional Retry instead of rows. */
  error?: ReactNode
  /** Retry callback rendered with the error panel. */
  onRetry?: () => void
  /** Empty-state heading when there are zero rows. Default 'No results'. */
  emptyTitle?: ReactNode
  /** Empty-state body/guidance. Default 'No rows match your filter.' */
  emptyMessage?: ReactNode
  /** Empty-state CTA (e.g. a Button to create the first record). */
  emptyAction?: ReactNode
  /**
   * Extra class(es) appended to every body `<tr>` (additive, opt-in). The
   * built-in row divider + `hover:bg-whisper` stay intact; this lets a caller
   * layer on app-specific row affordances without restyling the shared default.
   */
  rowClassName?: string
}

/**
 * DataTable — batteries-included: internal sort (asc→desc→off), global filter,
 * pagination, and explicit loading / empty / error states. Degrades gracefully:
 * a load failure shows an error panel with Retry rather than a blank grid.
 * (For a fully-controlled table, use `Table`.)
 */
export function DataTable<RowT extends Record<string, unknown>>({
  columns,
  data,
  searchable = true,
  searchPlaceholder = 'Filter…',
  pageSize = 0,
  initialSort = null,
  caption,
  rowKey,
  toolbar,
  className = '',
  loading = false,
  loadingRows = 6,
  error,
  onRetry,
  emptyTitle = 'No results',
  emptyMessage = 'No rows match your filter.',
  emptyAction,
  rowClassName = '',
}: DataTableProps<RowT>) {
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(initialSort)
  const [page, setPage] = useState(0)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return data
    return data.filter((row) =>
      columns.some((c) => String((c.filterValue ? c.filterValue(row) : row[c.key]) ?? '').toLowerCase().includes(q))
    )
  }, [data, query, columns])

  const sorted = useMemo(() => {
    if (!sort) return filtered
    const col = columns.find((c) => c.key === sort.key)
    const valueOf = (row: RowT) => (col?.sortAccessor ? col.sortAccessor(row) : (row[sort.key] as string | number))
    const arr = [...filtered].sort((a, b) => {
      const av = valueOf(a)
      const bv = valueOf(b)
      if (typeof av === 'number' && typeof bv === 'number') return av - bv
      return String(av ?? '').localeCompare(String(bv ?? ''), undefined, { numeric: true })
    })
    return sort.dir === 'desc' ? arr.reverse() : arr
  }, [filtered, sort, columns])

  const pages = pageSize ? Math.max(1, Math.ceil(sorted.length / pageSize)) : 1
  const safePage = Math.min(page, pages - 1)
  const rows = pageSize ? sorted.slice(safePage * pageSize, safePage * pageSize + pageSize) : sorted

  function toggleSort(key: string) {
    setPage(0)
    setSort((s) => (s && s.key === key ? (s.dir === 'asc' ? { key, dir: 'desc' } : null) : { key, dir: 'asc' }))
  }

  return (
    <div className={cn('flex flex-col min-w-0', className)}>
      {(searchable || toolbar) && (
        <div className="flex items-center gap-3 px-5 py-3 border-b border-line">
          {searchable && (
            <SearchInput
              shape="pill"
              size="sm"
              ariaLabel="Filter rows"
              placeholder={searchPlaceholder}
              value={query}
              onChange={(v) => { setQuery(v); setPage(0) }}
              className="w-full max-w-xs"
            />
          )}
          {toolbar && <div className="ml-auto flex items-center gap-2">{toolbar}</div>}
        </div>
      )}

      {loading ? (
        <SkeletonRows rows={loadingRows} columns={columns.length || 4} />
      ) : error ? (
        <div role="alert" className="px-5 py-12 text-center">
          <p className="font-display text-h4 text-strong m-0 mb-2">Couldn't load this data</p>
          <p className="m-0 mb-4 max-w-sm mx-auto text-ink-muted">{error}</p>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="px-4 py-2 rounded-control border border-line-strong bg-white text-strong hover:bg-whisper focus-visible:outline-none focus-visible:shadow-focus coarse:min-h-tap"
            >
              Try again
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              {caption && <caption className="sr-only">{caption}</caption>}
              <thead>
                <tr className="border-b border-line">
                  {columns.map((c) => {
                    const isSorted = sort?.key === c.key
                    const sortable = c.sortable !== false
                    return (
                      <th
                        key={c.key}
                        scope="col"
                        aria-sort={isSorted ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : sortable ? 'none' : undefined}
                        className={cn('py-3 px-5 text-xs font-semibold uppercase tracking-eyebrow text-ink-muted', c.align === 'end' ? 'text-right' : 'text-left')}
                      >
                        {sortable ? (
                          <button
                            type="button"
                            onClick={() => toggleSort(c.key)}
                            className={cn('inline-flex items-center gap-1 rounded hover:text-strong focus-visible:outline-none focus-visible:shadow-focus', c.align === 'end' && 'flex-row-reverse')}
                          >
                            {c.header}
                            {isSorted ? (sort!.dir === 'asc' ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />) : <IconChevronExpand size={14} className="opacity-40" />}
                          </button>
                        ) : (
                          c.header
                        )}
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={String(rowKey ? rowKey(row, i) : ((row.id as string | number) ?? i))} className={cn('border-b border-line last:border-0 transition-colors duration-fast ease-out-soft hover:bg-whisper', rowClassName)}>
                    {columns.map((c) => (
                      <td key={c.key} className={cn('py-3 px-5 text-ink-body align-middle', c.align === 'end' && 'text-right')}>
                        {c.render ? c.render(row) : (row[c.key] as ReactNode)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {rows.length === 0 && (
            <div role="status" aria-live="polite">
              <EmptyState title={emptyTitle} action={emptyAction}>{emptyMessage}</EmptyState>
            </div>
          )}
        </>
      )}

      {!loading && !error && pageSize > 0 && sorted.length > 0 && (
        <div className="flex items-center gap-3 px-5 py-3 border-t border-line text-sm text-ink-muted">
          <span>{sorted.length} rows · page {safePage + 1} of {pages}</span>
          <div className="ml-auto flex gap-2">
            <button type="button" disabled={safePage === 0} onClick={() => setPage(safePage - 1)} className="px-3 py-1 rounded-control border border-line disabled:opacity-40 hover:bg-whisper focus-visible:outline-none focus-visible:shadow-focus coarse:min-h-tap">Prev</button>
            <button type="button" disabled={safePage >= pages - 1} onClick={() => setPage(safePage + 1)} className="px-3 py-1 rounded-control border border-line disabled:opacity-40 hover:bg-whisper focus-visible:outline-none focus-visible:shadow-focus coarse:min-h-tap">Next</button>
          </div>
        </div>
      )}
    </div>
  )
}
